import React, { useRef } from "react";

import { Button } from "@newsela/angelou";
import PropTypes from "prop-types";

import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";

import {
  contentSettingsContainerCss,
  contentSettingsTextCss,
} from "./UniversalEmbedCardFooter.styles";

const getContentItemUrl = (productFamily, slug, contentId) =>
  productFamily === "google_classroom_addon"
    ? `${MONOLITH_ORIGIN}/apps/universal-embed/google-classroom/content/article/${slug}/${contentId}/`
    : `${MONOLITH_ORIGIN}/apps/lti-tool-provider/content/article/${slug}/${contentId}/`;

const UniversalEmbedCardFooter = ({
  // common
  contentId,
  title,
  slug,
  productFamily,

  // LTI only
  requestId,

  // Google Classroom Add-on only
  addOnToken,
  courseId,
  itemId,
  itemType,
}) => {
  const formRef = useRef(null);

  const isAddon = productFamily === "google_classroom_addon";
  const graphItemType = isAddon ? `ContentItem` : `LtiLinkItem`;
  const actionUrl = isAddon
    ? `${MONOLITH_ORIGIN}/apps/universal-embed/google-classroom/finish/`
    : `${MONOLITH_ORIGIN}/apps/universal-embed/finish/`;

  // One unified payload for both LTI and Google Classroom Add-on
  const buildContentItems = () => {
    const base = {
      "@type": graphItemType,
      "@id": getContentItemUrl(productFamily, slug, contentId),
      url: getContentItemUrl(productFamily, slug, contentId),
      title,
      text: title,
    };

    if (!isAddon) {
      base.mediaType = "application/vnd.ims.lti.v1.ltilink";
      base.placementAdvice = { presentationDocumentTarget: "window" };
    }

    return {
      "@context": "http://purl.imsglobal.org/ctx/lti/v1/ContentItem",
      "@graph": [base],
    };
  };

  const submitForm = (e) => {
    e.preventDefault();

    const formData = formRef.current;
    if (!formData) return;

    // content_items
    const contentItems = buildContentItems();
    formData.content_items.value = JSON.stringify(contentItems);

    if (isAddon) {
      // Only Classroom Add-on needs extra_params
      const extraParams = {
        addOnToken,
        courseId,
        itemId,
        itemType,
      };
      formData.extra_params.value = JSON.stringify(extraParams);
    } else {
      // Only LTI finish needs request_id; default to safe sentinel so server doesn't 400.
      formData.request_id.value = requestId || "NO_REQUEST_ID";
    }

    formData.submit();
  };

  return (
    <div css={contentSettingsContainerCss}>
      <div css={contentSettingsTextCss}>
        <form
          id={`responseForm${contentId}`}
          ref={formRef}
          action={actionUrl}
          method="post"
          data-testid="response-form"
        >
          {/* Always present */}
          <input
            type="hidden"
            name="content_items"
            id="content_items"
            data-testid="content-items"
          />

          {/* LTI-only (harmless to render conditionally) */}
          {!isAddon && (
            <input type="hidden" name="request_id" id="request_id" />
          )}

          {/* Add-on–only */}
          {isAddon && (
            <input type="hidden" name="extra_params" id="extra_params" />
          )}

          <Button type="button" onClick={submitForm}>
            Add link
          </Button>
        </form>
      </div>
    </div>
  );
};

UniversalEmbedCardFooter.propTypes = {
  productFamily: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  contentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,

  // LTI flow
  requestId: PropTypes.string,

  // Google Classroom Add-on flow
  addOnToken: PropTypes.string,
  courseId: PropTypes.string,
  itemId: PropTypes.string,
  itemType: PropTypes.string,
};

export default UniversalEmbedCardFooter;
